/**
 * Fuzzy String Matching Utilities
 *
 * Provides algorithms for matching strings with typos or slight variations.
 * Uses Levenshtein distance to calculate similarity scores.
 */

/**
 * Calculate Levenshtein distance (edit distance) between two strings
 *
 * Measures the minimum number of single-character edits (insertions, deletions, substitutions)
 * required to change one string into another.
 *
 * @param str1 - First string
 * @param str2 - Second string
 * @returns Number of edits needed (0 = identical)
 *
 * @example
 * levenshteinDistance('kitten', 'sitting') // 3
 * levenshteinDistance('tomato', 'tomatoe') // 1
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;

  // Create matrix for dynamic programming
  const matrix: number[][] = [];

  // Initialize first column (distance from empty string)
  for (let i = 0; i <= len2; i++) {
    matrix[i] = [i];
  }

  // Initialize first row (distance from empty string)
  for (let j = 0; j <= len1; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (let i = 1; i <= len2; i++) {
    for (let j = 1; j <= len1; j++) {
      // If characters match, no additional cost
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        // Minimum of:
        // - Substitution: matrix[i-1][j-1] + 1
        // - Insertion:    matrix[i][j-1] + 1
        // - Deletion:     matrix[i-1][j] + 1
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[len2][len1];
}

/**
 * Calculate similarity score as a percentage (0-100)
 *
 * Higher score = more similar
 * 100 = identical, 0 = completely different
 *
 * @param str1 - First string
 * @param str2 - Second string
 * @returns Similarity score (0-100)
 *
 * @example
 * similarityScore('tomato', 'tomato') // 100
 * similarityScore('tomato', 'tomatoe') // ~92.86
 * similarityScore('cucumber', 'tomato') // ~37.5
 */
export function similarityScore(str1: string, str2: string): number {
  // Normalize to lowercase for case-insensitive comparison
  const norm1 = str1.toLowerCase();
  const norm2 = str2.toLowerCase();

  // Calculate distance
  const distance = levenshteinDistance(norm1, norm2);

  // Calculate max possible distance (length of longer string)
  const maxLength = Math.max(norm1.length, norm2.length);

  // Avoid division by zero
  if (maxLength === 0) {
    return 100;
  }

  // Convert to percentage similarity
  return ((maxLength - distance) / maxLength) * 100;
}

/**
 * Check if two strings are a fuzzy match based on similarity threshold
 *
 * @param str1 - First string
 * @param str2 - Second string
 * @param threshold - Minimum similarity percentage (0-100), default 85%
 * @returns True if similarity score meets or exceeds threshold
 *
 * @example
 * isFuzzyMatch('tomato', 'tomatoe', 85) // true (92.86% similar)
 * isFuzzyMatch('cucumber', 'tomato', 85) // false (37.5% similar)
 * isFuzzyMatch('oninos', 'onions', 80) // true (83.33% similar)
 */
export function isFuzzyMatch(
  str1: string,
  str2: string,
  threshold: number = 85
): boolean {
  return similarityScore(str1, str2) >= threshold;
}

/**
 * Find the best fuzzy match from a list of candidates
 *
 * @param target - String to match
 * @param candidates - List of possible matches
 * @param threshold - Minimum similarity threshold (default 85%)
 * @returns Best match object with candidate string and score, or null if no match
 *
 * @example
 * findBestMatch('tomatoe', ['cucumber', 'tomato', 'onion'])
 * // { match: 'tomato', score: 92.86 }
 */
export function findBestMatch(
  target: string,
  candidates: string[],
  threshold: number = 85
): { match: string; score: number } | null {
  let bestMatch: string | null = null;
  let bestScore = 0;

  for (const candidate of candidates) {
    const score = similarityScore(target, candidate);

    if (score >= threshold && score > bestScore) {
      bestMatch = candidate;
      bestScore = score;
    }
  }

  if (bestMatch === null) {
    return null;
  }

  return { match: bestMatch, score: bestScore };
}

/**
 * Find all fuzzy matches from a list of candidates
 *
 * @param target - String to match
 * @param candidates - List of possible matches
 * @param threshold - Minimum similarity threshold (default 85%)
 * @returns Array of matches sorted by score (highest first)
 *
 * @example
 * findAllMatches('tomatoe', ['cucumber', 'tomato', 'tomatoes'], 80)
 * // [
 * //   { match: 'tomatoes', score: 93.75 },
 * //   { match: 'tomato', score: 92.86 }
 * // ]
 */
export function findAllMatches(
  target: string,
  candidates: string[],
  threshold: number = 85
): Array<{ match: string; score: number }> {
  const matches: Array<{ match: string; score: number }> = [];

  for (const candidate of candidates) {
    const score = similarityScore(target, candidate);

    if (score >= threshold) {
      matches.push({ match: candidate, score });
    }
  }

  // Sort by score (highest first)
  return matches.sort((a, b) => b.score - a.score);
}

/**
 * Check if a string is likely a typo of another (lenient threshold)
 *
 * Uses a lower threshold (75%) for detecting common typos
 *
 * @param original - Correct spelling
 * @param potential - Potential typo
 * @returns True if likely a typo
 *
 * @example
 * isLikelyTypo('cucumber', 'cucmber') // true
 * isLikelyTypo('onion', 'oninos') // true
 * isLikelyTypo('tomato', 'banana') // false
 */
export function isLikelyTypo(original: string, potential: string): boolean {
  return isFuzzyMatch(original, potential, 75);
}
